import {getKeyForFilePath} from '../keys';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {BaseFetchParams} from '.';

// This generic type is pretty confusing.
// Understanding the logic inside the function first is recommended.
type FetchWithCacheParams<ReturnType, PathResultType> = BaseFetchParams & {
  pathPromiseByKey: Map<string, Promise<PathResultType>>;
  fetchNew: (p: BaseFetchParams) => Promise<ReturnType>;
  getPromiseForPath: (
    fetchNewPromise: Promise<ReturnType>,
    filePath: string
  ) => Promise<PathResultType>;
  getReturnValue: (
    filePaths: string[],
    results: PathResultType[]
  ) => ReturnType;
};

// Use a global cache to deduplicate requests (including ongoing ones)
export async function fetchWithCache<
  ReturnType extends Map<string, string> | Set<string> | null,
  PathResultType,
>({
  runSignature,
  filePaths,
  pathPromiseByKey,
  fetchNew,
  getPromiseForPath,
  getReturnValue,
}: FetchWithCacheParams<
  ReturnType,
  PathResultType
>): Promise<ReturnType | null> {
  // Get paths that have not been requested before and perform actual request
  const newFilePaths = filePaths.filter(
    filePath => !pathPromiseByKey.has(getKeyForFilePath(runSignature, filePath))
  );
  const fetchNewPromise = fetchNew({
    runSignature,
    filePaths: newFilePaths,
  });

  // The result may be a mix of promises from previous requests (from the cache)
  // and promises from the new request launched above.
  const pathResultsPromise = Promise.all(
    filePaths.map(filePath => {
      const filePathKey = getKeyForFilePath(runSignature, filePath);

      // If this file path was included in a previous request,
      // get the resolved/resolving promise from the cache
      const fromCache = pathPromiseByKey.get(filePathKey);
      if (fromCache != null) {
        return fromCache;
      }

      // Otherwise, create a promise that waits for the new request to complete and
      // returns the appropriate value for this specific path.
      // The promise is stored in the cache immediately so that requests for the same path
      // that come in at the same time do not duplicate requests.
      const promise = getPromiseForPath(fetchNewPromise, filePath);
      pathPromiseByKey.set(filePathKey, promise);
      return promise;
    })
  );

  // For paths that were not handled properly by the request for one reason or another (possibly backend issue),
  // we delete their entry in the cache so that they will be retried in a future request the next time they're needed.
  const newMapOrSet = await fetchNewPromise;
  for (const filePath of newFilePaths) {
    if (!newMapOrSet?.has(filePath)) {
      const filePathKey = getKeyForFilePath(runSignature, filePath);
      pathPromiseByKey.delete(filePathKey);
    }
  }

  // Currently, the requests returning null means we have unexpected behavior.
  // We're returning `null` here to abort for now just to be safe.
  // TODO: potentially consider gracefully handling this and carrying on
  const pathResults = await pathResultsPromise;
  if (pathResults.some(e => e == null)) {
    return null;
  }

  // Transform path-specific data into the actual form that the cached function spits out.
  return getReturnValue(filePaths, pathResults);
}
